import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";

// ─── PILOT CAR STARTER BUNDLE — LANDING / PRICING PAGE ─────────────────────────
// Sells the $349 Starter Bundle: business setup, pricing, dispatcher ops,
// templates, and platform training — explicitly NOT exam prep or certification.
// Includes an interactive infographic-style module breakdown, a visual
// "your first 30 days" roadmap, an optional audio pitch (browser
// text-to-speech, no audio file hosting required), and a live upsell
// calculator for the 50%-off certification bonus.
//
// Matches the site's navy/gold enterprise token system and auto-detects
// light/dark mode the same way PeterQAWidget does — pass isDarkMode
// explicitly if this page is mounted under a route that already tracks
// theme (see your App.jsx pattern), otherwise it follows the `dark` class
// on <html> on its own.

const LIGHT_C = {
  page:"#f8fafc", surface:"#ffffff", surfaceAlt:"#f1f5f9",
  navy:"#0d1f3c", navyMid:"#1a3358",
  gold:"#c9971c", goldSoft:"#fef9ec",
  blue:"#1e56a8", blueSoft:"#dbeafe",
  green:"#059669", greenSoft:"#dcfce7",
  amber:"#d97706", amberSoft:"#fef3c7",
  purple:"#7c3aed", purpleSoft:"#ede9fe",
  teal:"#0891b2", tealSoft:"#cffafe",
  rose:"#e11d48", roseSoft:"#ffe4e6",
  text:"#0d1f3c", textMid:"#334155", textSub:"#475569", textLight:"#64748b",
  border:"#e2e8f0",
};
const DARK_C = {
  page:"#0a1628", surface:"#142a4d", surfaceAlt:"#1a3358",
  navy:"#0a1628", navyMid:"#142a4d",
  gold:"#fbbf24", goldSoft:"rgba(251,191,36,0.12)",
  blue:"#60a5fa", blueSoft:"rgba(96,165,250,0.14)",
  green:"#34d399", greenSoft:"rgba(52,211,153,0.14)",
  amber:"#fbbf24", amberSoft:"rgba(251,191,36,0.14)",
  purple:"#c4b5fd", purpleSoft:"rgba(196,181,253,0.14)",
  teal:"#22d3ee", tealSoft:"rgba(34,211,238,0.14)",
  rose:"#fb7185", roseSoft:"rgba(251,113,133,0.14)",
  text:"#f0f4f8", textMid:"rgba(240,244,248,0.85)", textSub:"rgba(240,244,248,0.65)", textLight:"rgba(240,244,248,0.5)",
  border:"rgba(255,255,255,0.10)",
};

function useAutoDarkMode(forced) {
  const [isDark, setIsDark] = useState(() => {
    if (typeof forced === "boolean") return forced;
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("dark");
  });
  useEffect(() => {
    if (typeof forced === "boolean") { setIsDark(forced); return; }
    if (typeof document === "undefined") return;
    const update = () => setIsDark(document.documentElement.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [forced]);
  return isDark;
}

// ── Module breakdown (mirrors the bundle spec you provided) ───────────────────
const MODULES = [
  {
    key:"starter", icon:"🚦", title:"Starter Guide", colorKey:"blue", note:"$49 value included",
    description:"Everything a beginner needs before the first job — equipment, safety, and communication basics.",
    voiceover:"The Starter Guide gets you job-ready from zero. You'll learn exactly what equipment to buy, how to read your first oversize permit, and the CB radio protocol escorts use on the road. We cover positioning — where to sit relative to the load — plus the most common beginner mistakes so you don't learn them the expensive way. Finish this module with a quick-start checklist you can run through before every job.",
    items:[
      { title:"Equipment basics", desc:"What to buy first — signage, lighting, height pole — and what to skip until you're busier." },
      { title:"Permit introduction", desc:"How to read an oversize load permit and pull out the details that affect your route." },
      { title:"First-job checklist", desc:"A run-through to complete the night before and morning of your first escort." },
      { title:"CB radio protocol", desc:"The channels, phrasing, and etiquette pilot cars actually use with truckers on the road." },
      { title:"Safety fundamentals", desc:"Following distance, blocking traffic, and staying visible in every condition." },
      { title:"Positioning basics", desc:"When to lead, when to follow, and how that changes with load width and turns." },
      { title:"Beginner mistakes", desc:"The most common first-month errors — and how to avoid each one." },
      { title:"Quick-start checklist", desc:"A printable checklist to run through before every job, beginner or not." },
    ],
  },
  {
    key:"business", icon:"🏢", title:"Business Setup Essentials", colorKey:"purple",
    description:"The paperwork done right from day one — not fixed later once it's already a problem.",
    voiceover:"Business Setup Essentials walks you through the four things every new pilot car operator needs to get right early. First, an LLC setup overview — how to structure your business and why it matters for liability. Second, EIN basics — the tax ID your business needs to invoice legally. Third, an insurance overview covering the coverage types companies expect you to carry. And fourth, a business registration overview so you're operating legally in your state from your very first job, not scrambling to fix it after you're already booked.",
    items:[
      { title:"LLC setup overview", desc:"How to structure your business for liability protection, and what it costs to set up." },
      { title:"EIN basics", desc:"Why you need a federal tax ID and how to get one in minutes." },
      { title:"Insurance overview", desc:"The coverage types and minimums companies typically expect a pilot car operator to carry." },
      { title:"Business registration overview", desc:"Registering legally in your state so you can invoice and operate without gaps." },
    ],
  },
  {
    key:"pricing", icon:"💵", title:"Pricing & Money", colorKey:"green",
    description:"Quote with confidence instead of guessing — includes a real negotiation script, not just theory.",
    voiceover:"Pricing and Money teaches you how to charge what you're worth. You'll learn how to price your services by mileage, load width, and wait time — not by guessing what sounds fair. We give you rate sheet templates you can hand to a client on the spot. Then we cover negotiation basics: how to hold your price without losing the job. And you'll walk through real QA dialogue examples — actual conversations between a driver and a client — so you know exactly what to say when someone tries to talk you down.",
    items:[
      { title:"How to price your services", desc:"Building a per-mile rate that accounts for load width, wait time, and repositioning." },
      { title:"Rate sheet templates", desc:"A ready-to-send rate sheet you can hand a client before you even quote a job." },
      { title:"Negotiation basics", desc:"How to hold your price without losing the job — and when it's fine to flex." },
      { title:"QA dialogue examples", desc:"Real driver-and-client conversations showing exactly what to say when a client pushes back on price." },
    ],
  },
  {
    key:"ops", icon:"📡", title:"Professional Operations", colorKey:"amber",
    description:"Run real jobs like a pro — enough to operate the business the moment your state certification comes through.",
    voiceover:"Professional Operations is where you learn to actually run jobs, not just talk about them. Dispatcher communication covers the exact radio and phone exchanges you'll have accepting a load, confirming pickup, and reporting your position. You'll learn multi-vehicle escort basics — how lead and chase cars coordinate on wide loads. We cover trip logs and workflow, the paperwork that keeps you organized and paid on time. And incident handling basics — what to say and do the moment something goes wrong on the road, calmly and by the book. Finish this module and you're ready to operate professionally the day your state certification comes through.",
    items:[
      { title:"Dispatcher communication", desc:"Sample scripts for accepting a load, confirming pickup, and checking in en route." },
      { title:"Accepting loads", desc:"What to confirm before you say yes — dimensions, route, and pay terms." },
      { title:"Multi-vehicle escort basics", desc:"How lead and chase cars coordinate positioning on wide or long loads." },
      { title:"Trip logs & workflow", desc:"Keeping records that get you paid on time and protect you if something's disputed." },
      { title:"Incident handling basics", desc:"What to say and do first if something goes wrong on the road." },
    ],
  },
  {
    key:"templates", icon:"🗂️", title:"Templates & Tools", colorKey:"teal",
    description:"Every document you need, ready to send — reused on every single job from here on.",
    voiceover:"Templates and Tools hands you seven ready-to-use documents. A contract template to protect both sides of the job. An invoice template so you get paid faster and look professional doing it. A rate card template you can customize and send in seconds. An equipment checklist and a daily inspection sheet to keep your vehicle job-ready. A trip log sheet to track every mile. And a company onboarding sheet for when a carrier wants to add you to their approved driver list.",
    items:[
      { title:"Contract template", desc:"A ready-to-use agreement that protects both you and the company you're working with." },
      { title:"Invoice template", desc:"A professional invoice you can customize and send in minutes." },
      { title:"Rate card template", desc:"A one-page rate card to send clients before they even ask." },
      { title:"Equipment checklist", desc:"What to inspect and carry before every job, in one printable list." },
      { title:"Daily inspection sheet", desc:"A quick pre-trip inspection log for your escort vehicle." },
      { title:"Trip log sheet", desc:"Track mileage, times, and route notes for every job you run." },
      { title:"Company onboarding sheet", desc:"What carriers ask for when adding you to their approved driver list." },
    ],
  },
  {
    key:"ecosystem", icon:"🌐", title:"PilotLinkUSA Ecosystem Training", colorKey:"rose",
    description:"Get comfortable with every tool on the platform before you need it on a real job.",
    voiceover:"The last module walks you through the PilotLinkUSA platform itself. You'll learn how to navigate the Marketplace to buy or sell equipment, use the Load Board to find and apply to jobs, complete Verification to earn your badge, work with Dispatcher tools if you're coordinating loads, use GPS tracking on an active job, and optimize your driver profile so companies find and hire you faster.",
    items:[
      { title:"Marketplace", desc:"Buying and selling escort equipment with other verified operators." },
      { title:"Load Board", desc:"Finding and applying to real oversize load jobs that match your certifications." },
      { title:"Verification", desc:"How the Verified Badge works and what it takes to earn one." },
      { title:"Dispatcher tools", desc:"What's available if you're coordinating loads, not just driving them." },
      { title:"GPS tracking", desc:"How live tracking works on an active job, for you and the company." },
      { title:"Driver profile optimization", desc:"Getting your profile in front of companies actively hiring." },
    ],
  },
];

const TOTAL_RESOURCES = MODULES.reduce((sum, m) => sum + m.items.length, 0);

// ── Sample dialogues — real, usable scripts, not just bullet points.
// Referenced inside the Pricing & Money and Professional Operations cards.
const SAMPLE_DIALOGUES = {
  pricing: {
    title:"Sample: negotiating a rate",
    lines:[
      { speaker:"Client", text:"We need a lead escort, Dallas to Houston, 12-foot-wide load, next Tuesday." },
      { speaker:"Driver", text:"For a 12-foot lead over that distance, my rate is $2.10 a mile, plus a $75 fee if pickup runs more than 30 minutes late." },
      { speaker:"Client", text:"Can you do $1.90 a mile?" },
      { speaker:"Driver", text:"I can do $1.95 if we lock in the return trip too — keeps us both efficient instead of me deadheading back." },
      { speaker:"Client", text:"Deal — $1.95, round trip." },
      { speaker:"Driver", text:"Sending the rate confirmation now." },
    ],
    lesson:"Notice the driver never just drops the price — they trade a small discount for something that benefits them too (the return trip). That's the negotiation habit this module is built around.",
  },
  ops: {
    title:"Sample: dispatcher communication",
    lines:[
      { speaker:"Dispatch", text:"PilotLink Dispatch to Escort 4, radio check." },
      { speaker:"Driver", text:"Escort 4, copy, loud and clear." },
      { speaker:"Dispatch", text:"You're cleared for pickup at mile marker 212. Wide load, 14 feet, height pole required." },
      { speaker:"Driver", text:"Copy that. Height pole's mounted, in position in 10 minutes." },
      { speaker:"Driver", text:"Dispatch, Escort 4 — debris in the lane ahead, requesting a route hold." },
      { speaker:"Dispatch", text:"Copy, holding traffic behind you. Advise when it's clear." },
    ],
    lesson:"Short, confirmed exchanges — no unnecessary chatter. This is the exact rhythm dispatchers and drivers use on a real job, including how to report a hazard calmly and get an immediate response.",
  },
};

// ── Your first 30 days — visual roadmap of the transformation, not just a
// feature list. This is the "make them believe it's worth it" section.
const ROADMAP = [
  { day:"Day 1", title:"Get equipment & safety right", desc:"Starter Guide gets you job-ready: gear, CB protocol, positioning, and the mistakes to avoid on your first run." },
  { day:"Week 1", title:"Set up your business properly", desc:"LLC, EIN, insurance, and registration — the paperwork done right from day one, not fixed later." },
  { day:"Week 2", title:"Price and win your first client", desc:"Use the rate sheet templates and negotiation scripts to quote with confidence instead of guessing." },
  { day:"Ongoing", title:"Run it like a pro", desc:"Contracts, invoices, trip logs, and dispatcher communication — every template you need, reused on every job." },
];

// ── Certification bonus add-ons (50% off, one course, buyer's choice) ─────────
// Prices confirmed against the live /pricing page (PricingPage.jsx COURSES):
// Single-State $129, NY Standalone $149, PEVO 14-State $249, PPCA Master $499.
const ADDON_COURSES = [
  { key:"single-state", label:"Single-State Certification", fullPrice:129, priceConfirmed:true },
  { key:"ny", label:"New York (NY) Certification", fullPrice:149, priceConfirmed:true },
  { key:"pevo", label:"PEVO Certification", fullPrice:249, priceConfirmed:true },
  { key:"ppca", label:"PPCA Certification", fullPrice:499, priceConfirmed:true },
];

const BASE_PRICE = 349;

function money(n) {
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
}

function StatChip({ icon, value, label, C }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:"12px 16px" }}>
      <span style={{ fontSize:22 }} aria-hidden="true">{icon}</span>
      <div>
        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:18, color:C.text, lineHeight:1 }}>{value}</div>
        <div style={{ fontSize:11, color:C.textSub }}>{label}</div>
      </div>
    </div>
  );
}

// Rough duration estimate for narration — 150 words/min is a standard pace
// for clear, well-paced training voiceover (a touch slower than
// conversational speech, which runs closer to 160-180 wpm).
function estimateDuration(text) {
  const words = text.trim().split(/\s+/).length;
  const seconds = Math.round((words / 150) * 60);
  return { words, seconds, label: seconds >= 60 ? `~${Math.round(seconds / 60)} min` : `~${seconds}s` };
}

function ListenButton({ text, label, C, compact }) {
  const [speaking, setSpeaking] = useState(false);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  const toggle = () => {
    if (!supported) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.98;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    setSpeaking(true);
  };

  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);

  if (!supported) return null;

  return (
    <button onClick={toggle} aria-pressed={speaking} style={{
      display:"inline-flex", alignItems:"center", gap:compact ? 6 : 8,
      padding: compact ? "6px 12px" : "10px 18px", borderRadius:99,
      border:`1.5px solid ${C.border}`, background:C.surface, color:C.textMid,
      fontSize: compact ? 11.5 : 13, fontWeight:700, cursor:"pointer", flexShrink:0,
    }}>
      <span aria-hidden="true">{speaking ? "⏹️" : "🔊"}</span>
      {label}
    </button>
  );
}

function DialogueCard({ dialogue, color, C }) {
  return (
    <div style={{ marginTop:12, background:`${color}0d`, border:`1px solid ${color}30`, borderRadius:12, padding:"14px 16px" }}>
      <div style={{ fontSize:11.5, fontWeight:800, color, marginBottom:10 }}>💬 {dialogue.title}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:7, marginBottom:10 }}>
        {dialogue.lines.map((l, i) => (
          <div key={i} style={{ fontSize:12.5, lineHeight:1.6 }}>
            <span style={{ fontWeight:800, color:C.text }}>{l.speaker}: </span>
            <span style={{ color:C.textMid }}>"{l.text}"</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize:11.5, color:C.textSub, fontStyle:"italic", lineHeight:1.6, borderTop:`1px dashed ${color}30`, paddingTop:9 }}>
        💡 {dialogue.lesson}
      </div>
    </div>
  );
}

function ModuleCard({ m, C }) {
  const color = C[m.colorKey];
  const soft = C[m.colorKey + "Soft"];
  const [open, setOpen] = useState(false);
  const duration = estimateDuration(m.voiceover);
  const dialogue = SAMPLE_DIALOGUES[m.key];

  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden", borderTop:`3px solid ${color}` }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{ width:"100%", textAlign:"left", background:"none", border:"none", cursor:"pointer", padding:"18px 20px", display:"flex", alignItems:"flex-start", gap:12, fontFamily:"inherit" }}
      >
        <span style={{ fontSize:26, flexShrink:0 }} aria-hidden="true">{m.icon}</span>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:15.5, color:C.text }}>{m.title}</span>
            {m.note && <span style={{ fontSize:10.5, fontWeight:800, color, background:soft, padding:"2px 9px", borderRadius:99 }}>{m.note}</span>}
          </div>
          <div style={{ fontSize:12, color:C.textSub, marginTop:3 }}>{m.description}</div>
          <div style={{ fontSize:11, color:C.textLight, marginTop:3 }}>{m.items.length} resources included</div>
        </div>
        <span aria-hidden="true" style={{ color:C.textLight, fontSize:16, transform: open ? "rotate(180deg)" : "none", transition:"transform .15s", flexShrink:0 }}>⌄</span>
      </button>
      {open && (
        <div style={{ padding:"0 20px 20px 58px" }}>
          <div style={{ marginBottom:12 }}>
            <ListenButton text={m.voiceover} label={`Listen to this module (${duration.label})`} C={C} compact />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
            {m.items.map(item => (
              <div key={item.title} style={{ display:"flex", gap:8, fontSize:13 }}>
                <span style={{ color, fontWeight:800, flexShrink:0 }} aria-hidden="true">✓</span>
                <div>
                  <span style={{ color:C.text, fontWeight:700 }}>{item.title}</span>
                  <div style={{ color:C.textSub, fontSize:12, marginTop:1, lineHeight:1.5 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
          {dialogue && <DialogueCard dialogue={dialogue} color={color} C={C} />}
        </div>
      )}
    </div>
  );
}

const HERO_SCRIPT = "The Pilot Car Starter Bundle. Three hundred forty nine dollars. " +
  "This is the professional upgrade for new pilot car drivers. " +
  "You get the Starter Guide, business setup essentials, pricing and negotiation training, " +
  "dispatcher and operations training, seven ready to use templates, and full PilotLink U S A ecosystem training. " +
  "Plus, fifty percent off any one certification course as a bonus. " +
  "This bundle does not include exam prep or compliance content — it's built to get your business running, " +
  "not to certify you.";

export default function StarterBundleLanding({ isDarkMode }) {
  const isDark = useAutoDarkMode(isDarkMode);
  const C = isDark ? DARK_C : LIGHT_C;

  const [addon, setAddon] = useState(null);
  const selected = ADDON_COURSES.find(a => a.key === addon) || null;
  const addonDiscountedPrice = selected ? selected.fullPrice / 2 : 0;
  const total = BASE_PRICE + addonDiscountedPrice;

  return (
    <div style={{ background:C.page, minHeight:"100vh", transition:"background .3s", fontFamily:"'Inter',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@700;800;900&display=swap');
        * { box-sizing: border-box; }
        button, input { font-family: inherit; }
        button:focus-visible, input:focus-visible, a:focus-visible { outline: 2px solid #c9971c; outline-offset: 2px; }
        .bundle-stats { display:grid; grid-template-columns: repeat(4, 1fr); gap:12px; }
        .bundle-modules { display:grid; grid-template-columns: repeat(2, 1fr); gap:16px; }
        .bundle-roadmap { display:grid; grid-template-columns: repeat(4, 1fr); gap:16px; }
        .bundle-addons { display:grid; grid-template-columns: repeat(2, 1fr); gap:10px; }
        @media (max-width: 900px) {
          .bundle-stats { grid-template-columns: repeat(2, 1fr); }
          .bundle-modules { grid-template-columns: 1fr; }
          .bundle-roadmap { grid-template-columns: 1fr; }
          .bundle-addons { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* ── HERO ── */}
      <div style={{ background:"linear-gradient(160deg, #0d1f3c, #1a3358 60%, #0d1f3c)", padding:"64px 24px 56px" }}>
        <div style={{ maxWidth:840, margin:"0 auto", textAlign:"center" }}>
          <div style={{ display:"inline-block", fontSize:11, fontWeight:800, color:"#fde68a", letterSpacing:".14em", background:"rgba(201,151,28,0.15)", border:"1px solid rgba(201,151,28,0.35)", padding:"6px 16px", borderRadius:99, marginBottom:20 }}>
            PROFESSIONAL UPGRADE FOR NEW DRIVERS
          </div>
          <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(28px,4.5vw,44px)", fontWeight:900, color:"#fff", lineHeight:1.15, margin:"0 0 16px" }}>
            Start Your Pilot Car Career the Right Way
          </h1>
          <p style={{ fontSize:16, color:"rgba(255,255,255,0.75)", maxWidth:600, margin:"0 auto 28px", lineHeight:1.7 }}>
            Learn how to operate, communicate, price, and build your business — everything but the exam.
          </p>

          <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center", gap:10, marginBottom:24 }}>
            <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:52, fontWeight:900, color:"#fff" }}>$349</span>
            <span style={{ fontSize:14, color:"rgba(255,255,255,0.6)" }}>one-time payment</span>
          </div>

          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <a href="#addons" style={{ background:"#c9971c", color:"#0d1f3c", fontWeight:800, fontSize:15, padding:"14px 30px", borderRadius:99, textDecoration:"none", boxShadow:"0 8px 24px rgba(201,151,28,0.35)" }}>
              Start Bundle — $349 →
            </a>
            <ListenButton
              text={HERO_SCRIPT}
              label={`Listen to the pitch (${estimateDuration(HERO_SCRIPT).label})`}
              C={{ ...DARK_C, surface:"rgba(255,255,255,0.06)", border:"rgba(255,255,255,0.2)", textMid:"#fff" }}
            />
          </div>
        </div>
      </div>

      <div style={{ maxWidth:960, margin:"0 auto", padding:"40px 24px 80px" }}>

        {/* ── STAT STRIP (infographic-style quick scan) ── */}
        <div className="bundle-stats" style={{ marginTop:-52, marginBottom:44 }}>
          <StatChip icon="🧩" value="6" label="Training modules" C={C} />
          <StatChip icon="📎" value={`${TOTAL_RESOURCES}+`} label="Lessons & resources" C={C} />
          <StatChip icon="🗂️" value="7" label="Ready-to-use templates" C={C} />
          <StatChip icon="🎁" value="50%" label="Off any certification" C={C} />
        </div>

        {/* ── WHAT YOU GET ── */}
        <div style={{ marginBottom:52 }}>
          <div style={{ textAlign:"center", marginBottom:28 }}>
            <div style={{ fontSize:11, fontWeight:800, color:C.gold, letterSpacing:".12em", marginBottom:8 }}>WHAT YOU GET</div>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:28, fontWeight:800, color:C.text }}>Everything to launch your business</h2>
            <p style={{ fontSize:14, color:C.textSub, maxWidth:560, margin:"10px auto 0" }}>Tap a module to see exactly what's inside — and listen to a short voiceover for each one.</p>
            <p style={{ fontSize:11.5, color:C.textLight, marginTop:8 }}>
              🎧 Full audio walkthrough, all 6 modules: ~{Math.round(MODULES.reduce((s, m) => s + estimateDuration(m.voiceover).seconds, 0) / 60)} min total
            </p>
          </div>
          <div className="bundle-modules">
            {MODULES.map(m => <ModuleCard key={m.key} m={m} C={C} />)}
          </div>
        </div>

        {/* ── ROADMAP ── */}
        <div style={{ marginBottom:52 }}>
          <div style={{ textAlign:"center", marginBottom:28 }}>
            <div style={{ fontSize:11, fontWeight:800, color:C.blue, letterSpacing:".12em", marginBottom:8 }}>YOUR FIRST 30 DAYS</div>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:28, fontWeight:800, color:C.text }}>From "new driver" to "running a business"</h2>
          </div>
          <div className="bundle-roadmap">
            {ROADMAP.map((r, i) => (
              <div key={r.day} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 18px", position:"relative" }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:C.goldSoft, border:`1.5px solid ${C.gold}`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:13, color:C.gold, marginBottom:12 }}>
                  {i + 1}
                </div>
                <div style={{ fontSize:10.5, fontWeight:800, color:C.gold, letterSpacing:".08em", marginBottom:4 }}>{r.day.toUpperCase()}</div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:14.5, color:C.text, marginBottom:6, lineHeight:1.3 }}>{r.title}</div>
                <div style={{ fontSize:12.5, color:C.textSub, lineHeight:1.6 }}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── UPSELL: CERTIFICATION BONUS CALCULATOR ── */}
        <div id="addons" style={{ marginBottom:52, background:"linear-gradient(135deg, #0d1f3c, #1a3358)", borderRadius:24, padding:"32px 28px" }}>
          <div style={{ textAlign:"center", marginBottom:22 }}>
            <div style={{ fontSize:11, fontWeight:800, color:"#fde68a", letterSpacing:".12em", marginBottom:8 }}>⭐ EXCLUSIVE BONUS</div>
            <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:24, fontWeight:800, color:"#fff", marginBottom:8 }}>Add a certification course at 50% off</h2>
            <p style={{ fontSize:13.5, color:"rgba(255,255,255,0.7)", maxWidth:520, margin:"0 auto" }}>
              Optional — pick one course now and lock in half price. You can also add this later at full price.
            </p>
          </div>

          <div className="bundle-addons" style={{ maxWidth:640, margin:"0 auto 20px" }}>
            <button
              onClick={() => setAddon(null)}
              aria-pressed={addon === null}
              style={{
                textAlign:"left", padding:"14px 16px", borderRadius:14, cursor:"pointer",
                border:`1.5px solid ${addon === null ? "#c9971c" : "rgba(255,255,255,0.18)"}`,
                background: addon === null ? "rgba(201,151,28,0.12)" : "rgba(255,255,255,0.04)",
              }}
            >
              <div style={{ fontWeight:800, fontSize:13.5, color:"#fff" }}>No thanks, just the bundle</div>
              <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.55)", marginTop:2 }}>You can always add a course later</div>
            </button>
            {ADDON_COURSES.map(course => (
              <button
                key={course.key}
                onClick={() => setAddon(course.key)}
                aria-pressed={addon === course.key}
                style={{
                  textAlign:"left", padding:"14px 16px", borderRadius:14, cursor:"pointer",
                  border:`1.5px solid ${addon === course.key ? "#c9971c" : "rgba(255,255,255,0.18)"}`,
                  background: addon === course.key ? "rgba(201,151,28,0.12)" : "rgba(255,255,255,0.04)",
                }}
              >
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                  <div style={{ fontWeight:800, fontSize:13.5, color:"#fff" }}>{course.label}</div>
                  {!course.priceConfirmed && (
                    <span style={{ fontSize:9, fontWeight:800, color:"#fbbf24", background:"rgba(251,191,36,0.15)", padding:"2px 6px", borderRadius:6 }}>PRICE TBD</span>
                  )}
                </div>
                <div style={{ fontSize:12, marginTop:4 }}>
                  <span style={{ color:"rgba(255,255,255,0.45)", textDecoration:"line-through", marginRight:6 }}>{money(course.fullPrice)}</span>
                  <span style={{ color:"#34d399", fontWeight:800 }}>{money(course.fullPrice / 2)} today</span>
                </div>
              </button>
            ))}
          </div>

          {/* Running total */}
          <div style={{ maxWidth:360, margin:"0 auto", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:16, padding:"18px 20px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12.5, color:"rgba(255,255,255,0.7)", marginBottom:6 }}>
              <span>Starter Bundle</span><span>{money(BASE_PRICE)}</span>
            </div>
            {selected && (
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12.5, color:"rgba(255,255,255,0.7)", marginBottom:6 }}>
                <span>{selected.label} (50% off)</span><span>{money(addonDiscountedPrice)}</span>
              </div>
            )}
            <div style={{ borderTop:"1px solid rgba(255,255,255,0.15)", marginTop:8, paddingTop:10, display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#fff" }}>Total</span>
              <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:24, fontWeight:900, color:"#fde68a" }}>{money(total)}</span>
            </div>
          </div>

          <div style={{ textAlign:"center", marginTop:20 }}>
            <Link to="/signup" style={{ display:"inline-block", background:"#c9971c", color:"#0d1f3c", fontWeight:800, fontSize:15, padding:"14px 32px", borderRadius:99, textDecoration:"none" }}>
              {selected ? `Start Bundle + ${selected.label} — ${money(total)} →` : `Start Bundle — ${money(total)} →`}
            </Link>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:10 }}>One-time payment · Instant access</div>
          </div>
        </div>

        {/* ── PERFECT FOR / NOT INCLUDED ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:16, marginBottom:24 }}>
          <div style={{ background:C.greenSoft, border:`1px solid ${C.green}40`, borderRadius:16, padding:"20px 22px" }}>
            <div style={{ fontWeight:800, fontSize:14, color:C.green, marginBottom:8 }}>✔ Perfect for</div>
            <p style={{ fontSize:13, color:C.textMid, lineHeight:1.7, margin:0 }}>
              New drivers who want to start working and build a real business — not just pass an exam.
            </p>
          </div>
          <div style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:16, padding:"20px 22px" }}>
            <div style={{ fontWeight:800, fontSize:14, color:C.textSub, marginBottom:8 }}>🚫 Not included</div>
            <p style={{ fontSize:13, color:C.textMid, lineHeight:1.7, margin:0 }}>
              Exam prep, compliance content, and certification-level training aren't part of this bundle — that's what the certification bonus above is for.
            </p>
          </div>
        </div>

        <div style={{ textAlign:"center" }}>
          <Link to="/signup" style={{ display:"inline-block", background:C.navy, color:"#fff", fontWeight:800, fontSize:14, padding:"13px 28px", borderRadius:99, textDecoration:"none" }}>
            Start Bundle →
          </Link>
        </div>
      </div>
    </div>
  );
}
